import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { Logo } from '@/components/brand/Logo';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';

const loginSchema = z.object({
  email: z.string().min(1, 'Email wajib diisi.').email('Format email tidak valid.'),
  password: z.string().min(1, 'Kata sandi wajib diisi.'),
});

const registerSchema = z.object({
  name: z.string().min(2, 'Nama minimal 2 karakter.').max(80),
  email: z.string().min(1, 'Email wajib diisi.').email('Format email tidak valid.'),
  password: z.string().min(8, 'Kata sandi minimal 8 karakter.'),
});

/**
 * Halaman Masuk / Daftar dengan tab, mengikuti rancangan.
 *
 * Tambahan dari rancangan: indikator kekuatan kata sandi, tombol
 * perlihat/sembunyikan, dan galat per-field dari server yang dipetakan
 * langsung ke input yang bersangkutan.
 */
export const AuthPage = ({ mode: initialMode = 'login' }) => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { login, register: registerUser } = useAuthStore();

  const isLogin = mode === 'login';

  const form = useForm({
    resolver: zodResolver(isLogin ? loginSchema : registerSchema),
    mode: 'onBlur',
  });

  const password = form.watch('password') ?? '';

  const onSubmit = async (values) => {
    try {
      if (isLogin) {
        await login(values);
        navigate(searchParams.get('redirect') ?? '/dashboard', { replace: true });
        return;
      }

      // Pendaftaran TIDAK langsung masuk dashboard. Server menahan sesinya
      // sampai alamat email dikonfirmasi, jadi mengarahkan ke dashboard di
      // sini cuma akan memantulkan pengguna kembali ke halaman masuk.
      const hasil = await registerUser(values);
      navigate('/periksa-email', {
        replace: true,
        state: { email: hasil?.email ?? values.email, terkirim: hasil?.terkirim, pesan: hasil?.pesan },
      });
    } catch (err) {
      // Masuk yang ditolak karena email belum dikonfirmasi bukan kesalahan
      // pengetikan. Pengguna diantar ke halaman yang bisa mengirim ulang
      // tautannya, bukan diberi pesan merah di bawah tombol.
      if (err instanceof ApiError && err.details?.alasan === 'email_belum_terverifikasi') {
        navigate('/periksa-email', {
          replace: true,
          state: { email: err.details.email, pesan: err.message },
        });
        return;
      }

      if (err instanceof ApiError) {
        // Galat validasi dipasang ke field-nya masing-masing; sisanya jadi
        // pesan di atas tombol.
        const fieldErrors = err.fieldErrors;
        if (Object.keys(fieldErrors).length) {
          for (const [field, message] of Object.entries(fieldErrors)) {
            form.setError(field, { message });
          }
        } else {
          form.setError('root', { message: err.message });
        }
      } else {
        form.setError('root', { message: 'Terjadi kesalahan tak terduga.' });
      }
    }
  };

  const switchMode = (next) => {
    setMode(next);
    form.reset();
    form.clearErrors();
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--surface-canvas)] px-4 py-10">
      <Link to="/" className="mb-7">
        <Logo />
      </Link>

      <div className="w-full max-w-md rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-6 shadow-sm sm:p-7">
        {/* Tab Masuk / Daftar, sesuai rancangan. */}
        <div
          role="tablist"
          aria-label="Pilih Masuk atau Daftar"
          className="mb-6 grid grid-cols-2 gap-1 rounded-lg bg-[var(--surface-sunken)] p-1"
        >
          {[
            ['login', 'Masuk'],
            ['register', 'Daftar'],
          ].map(([value, label]) => (
            <button
              key={value}
              role="tab"
              type="button"
              aria-selected={mode === value}
              onClick={() => switchMode(value)}
              className={cn(
                'rounded-md py-2 text-sm font-semibold transition-all duration-200',
                mode === value
                  ? 'bg-[var(--surface-raised)] text-[var(--text-primary)] shadow-xs'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {!isLogin && (
            <Field label="Nama Lengkap" required error={form.formState.errors.name?.message}>
              {(props) => (
                <Input
                  {...props}
                  {...form.register('name')}
                  placeholder="Nama Anda"
                  autoComplete="name"
                  error={form.formState.errors.name}
                />
              )}
            </Field>
          )}

          <Field label="Email" required error={form.formState.errors.email?.message}>
            {(props) => (
              <Input
                {...props}
                {...form.register('email')}
                type="email"
                placeholder="nama@perusahaan.com"
                autoComplete="email"
                error={form.formState.errors.email}
              />
            )}
          </Field>

          <Field
            label="Kata Sandi"
            required
            error={form.formState.errors.password?.message}
            hint={!isLogin ? 'Minimal 8 karakter. Frasa panjang lebih aman daripada simbol rumit.' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                {...form.register('password')}
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                error={form.formState.errors.password}
                iconRight={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Sembunyikan kata sandi' : 'Perlihatkan kata sandi'}
                    className="rounded-md p-1 text-[var(--text-muted)] transition-colors hover:text-[var(--text-secondary)]"
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                }
              />
            )}
          </Field>

          {!isLogin && password && <PasswordStrength password={password} />}

          {isLogin && (
            <div className="flex justify-end">
              <Link
                to="/lupa-sandi"
                className="text-xs font-semibold text-accent-600 hover:underline dark:text-accent-400"
              >
                Lupa kata sandi?
              </Link>
            </div>
          )}

          {form.formState.errors.root && (
            <p
              role="alert"
              className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-2.5 text-sm font-medium text-danger-700 dark:border-danger-500/25 dark:bg-danger-500/10 dark:text-danger-500"
            >
              {form.formState.errors.root.message}
            </p>
          )}

          <Button
            type="submit"
            className="w-full"
            size="lg"
            loading={form.formState.isSubmitting}
          >
            {isLogin ? 'Masuk' : 'Buat Akun'}
          </Button>
        </form>
      </div>

      <Link
        to="/"
        className="mt-6 inline-flex items-center gap-1.5 text-sm text-[var(--text-muted)] transition-colors hover:text-[var(--text-secondary)]"
      >
        <ArrowLeft className="size-3.5" />
        Kembali ke beranda
      </Link>
    </div>
  );
};

const evaluate = (password) => {
  let score = 0;
  const suggestions = [];

  if (password.length >= 8) score += 1;
  else suggestions.push('gunakan minimal 8 karakter');

  if (password.length >= 12) score += 1;
  else if (password.length >= 8) suggestions.push('12 karakter lebih aman');

  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  else suggestions.push('campur huruf besar dan kecil');

  if (/\d/.test(password) || /[^\w\s]/.test(password)) score += 1;
  else suggestions.push('tambahkan angka atau simbol');

  if (/^(012|123|abc|qwerty|password)/i.test(password)) {
    score = Math.min(score, 1);
    suggestions.unshift('hindari pola yang mudah ditebak');
  }

  return {
    score,
    label: ['Sangat lemah', 'Lemah', 'Cukup', 'Kuat', 'Sangat kuat'][score],
    suggestion: suggestions[0],
  };
};

/**
 * Indikator kekuatan kata sandi. Menunjukkan kemajuan secara visual sekaligus
 * memberi satu saran konkret - bar tanpa penjelasan hanya membuat frustrasi.
 */
const PasswordStrength = ({ password }) => {
  const { score, label, suggestion } = evaluate(password);
  const colors = ['bg-danger-500', 'bg-danger-500', 'bg-warning-500', 'bg-success-500', 'bg-success-600'];

  return (
    <div>
      <div className="flex gap-1" role="presentation">
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors duration-300',
              index < score ? colors[score] : 'bg-[var(--border-subtle)]',
            )}
          />
        ))}
      </div>
      <p className="mt-1.5 text-xs text-[var(--text-muted)]" aria-live="polite">
        Kekuatan: <span className="font-semibold">{label}</span>
        {suggestion && ` - ${suggestion}`}
      </p>
    </div>
  );
};

